/**
 * JSX components for tool message formatters.
 *
 * Tools' `message: { call, result, error }` callbacks may return JSX. The
 * framework runs the JSX through `renderInstructions` to produce the
 * markdown the LLM sees. These components are the canonical building
 * blocks — composing them keeps the wire shape consistent across tools
 * without resorting to string-concatenation helpers.
 *
 * - `<DefaultToolCall>` — fallback rendering for `message.call` when a
 *   tool doesn't declare its own.
 * - `<DefaultToolError>` — fallback rendering for `message.error` when a
 *   tool doesn't declare its own.
 */
import type { ReactNode } from 'react';

import { Toon } from '#plugin-chat/lib/instructions/components.tsx';
import type { DatasetSummary } from '#plugin-chat/lib/tool/dataset-summary.ts';
import type { ToolOutputError } from '#plugin-chat/lib/tool/output-types.ts';

interface DefaultToolCallProps {
  args: Record<string, unknown>;
  toolName: string;
}

/**
 * Default `message.call` rendering — used when a tool doesn't declare
 * its own. Wraps a TOON-encoded args block in a `<pre>`.
 */
export function DefaultToolCall({ args, toolName }: DefaultToolCallProps) {
  return (
    <>
      <p>
        Invoking the <code>{toolName}</code> tool with the following parameters:
      </p>
      <Toon value={args} />
    </>
  );
}

interface DefaultToolErrorProps {
  args: Record<string, unknown>;
  envelope: ToolOutputError;
  toolName: string;
}

interface DefaultToolDeniedProps {
  args: Record<string, unknown>;
  reason: string;
  toolName: string;
}

interface ToolErrorMessageProps {
  toolName: string;
  envelope: ToolOutputError;
  /**
   * Optional tool-specific recovery guidance, rendered between the
   * standard "failed (errorType): message" line and the structured
   * suggestion list. Lets a tool add loop-friendly "fix X and retry"
   * prose without re-implementing the surrounding scaffold.
   */
  recovery?: ReactNode;
}

/**
 * Shared scaffold for failure transcript prose: the canonical
 * "failed (errorType): message" line, an optional tool-specific
 * `recovery` slot, and the structured `suggestions` list. Single source
 * of truth for failure rendering — {@link DefaultToolError} and every
 * per-tool `message.error` formatter compose this rather than repeating
 * the markup.
 */
export function ToolErrorMessage({ toolName, envelope, recovery }: ToolErrorMessageProps) {
  const suggestions = envelope.suggestions ?? [];
  return (
    <>
      <p>
        The <code>{toolName}</code> tool call failed ({envelope.errorType}): {envelope.error}
      </p>
      {recovery}
      {suggestions.length > 0 && (
        <>
          <p>Suggestions:</p>
          <ul>
            {suggestions.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
/**
 * Default `message.error` rendering — used when a tool doesn't declare
 * its own. Thin wrapper over {@link ToolErrorMessage} with no
 * tool-specific recovery slot. Covers both tool-level failures and
 * protocol errors, since both reach this component through the same
 * {@link ToolOutputError} envelope.
 */
export function DefaultToolError({ envelope, toolName }: DefaultToolErrorProps) {
  return <ToolErrorMessage envelope={envelope} toolName={toolName} />;
}

/**
 * Default `message.denied` rendering — used when a tool doesn't declare
 * its own. Surfaces the human approval-refusal reason so the LLM sees
 * why the call did not run and can adjust its plan.
 */
export function DefaultToolDenied({ reason, toolName }: DefaultToolDeniedProps) {
  return (
    <p>
      The <code>{toolName}</code> tool call was denied by the user: {reason}
    </p>
  );
}
interface DatasetPreviewProps {
  summary: DatasetSummary;
}

/**
 * Canonical LLM-facing rendering of a materialised dataset: a per-column
 * profile table (type, type-aware stats) followed by a
 * capped sample table. Pure JSX consumed by the instructions reconciler,
 * which turns it into GFM markdown tables — the model reads tables far
 * more reliably than the prior TOON block this replaces. Generic over
 * any {@link DatasetSummary}; not QKG-specific.
 */
export function DatasetPreview({ summary }: DatasetPreviewProps) {
  const { rowCount, columns, sample, notes } = summary;
  const colNames = columns.map((c) => c.name);
  const rowsLabel = (n: number): string => `${n} ${n === 1 ? 'row' : 'rows'}`;
  return (
    <>
      <p>{`${rowsLabel(rowCount)} × ${columns.length} ${columns.length === 1 ? 'column' : 'columns'}.`}</p>
      {notes.length > 0 && (
        <ul>
          {notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
      {columns.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Type</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c) => (
              <tr key={c.name}>
                <td>{c.name}</td>
                <td>{c.type}</td>
                <td>{c.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {sample.length > 0 && (
        <>
          <p>
            {`Sample — ${sample.length} of ${rowsLabel(rowCount)} (shape inspection only; do not derive counts, distinct values, or aggregates from these rows).`}
          </p>
          <table>
            <thead>
              <tr>
                {colNames.map((n) => (
                  <th key={n}>{n}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sample.map((row, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static, never-reordered sample list rendered once through the markdown reconciler (keys are inert in output)
                <tr key={`row-${i}`}>
                  {colNames.map((n) => (
                    <td key={n}>{JSON.stringify(row[n] ?? '')}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
